import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { makeOwnerGate } from '../lib/ownerGate';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { BrandLoader } from '../components/BrandLoader';
import { studiesApi, requestTypeSettingsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, FileText, FlaskConical, Clock, Users, CheckCircle2,
  XCircle, AlertCircle, ChevronRight, ChevronDown, History, Loader2, FileCheck, ShieldCheck, Pencil
} from 'lucide-react';
import IRBPanel from '../components/IRBPanel';
import { Input } from '../components/ui/input';

const STATUS_COLORS = {
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  ON_HOLD: 'bg-amber-100 text-amber-700',
  COMPLETED: 'bg-blue-100 text-blue-700',
  ARCHIVED: 'bg-slate-100 text-slate-500',
};

const REQ_STATUS_COLORS = {
  SUBMITTED: 'bg-blue-100 text-blue-700', ROUTED_TO_CONSULTANTS: 'bg-teal-100 text-teal-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700', COMPLETED: 'bg-emerald-100 text-emerald-700',
  EXECUTED: 'bg-emerald-100 text-emerald-800', FAILED: 'bg-red-100 text-red-700',
  REJECTED: 'bg-red-100 text-red-700', CHANGES_REQUESTED: 'bg-amber-100 text-amber-700',
  WITHDRAWN: 'bg-slate-100 text-slate-500', AWAITING_CHAIRMAN_ASSIGNMENT: 'bg-blue-100 text-blue-600',
  COORDINATOR_ASSIGNED: 'bg-violet-100 text-violet-700', UNDER_REVIEW: 'bg-yellow-100 text-yellow-700',
  PENDING_FINAL_APPROVAL: 'bg-green-100 text-green-700', PENDING_DIRECTOR_REVIEW: 'bg-indigo-100 text-indigo-700',
  PENDING_EXEC_DIRECTOR_REVIEW: 'bg-indigo-100 text-indigo-800', PENDING_SIGNATURES: 'bg-purple-100 text-purple-700',
  RESUBMITTED: 'bg-blue-100 text-blue-600',
};

const formatStatus = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

// iter167 Issue 35 — Same terminal-state list the backend uses when counting
// active requests toward the per-study cap. Requests in these states do NOT
// count, so a Study can always recover from a rejection/failure by creating
// a fresh request of the same type.
const TERMINAL_STATUSES = new Set(['REJECTED', 'WITHDRAWN', 'FAILED', 'CLOSED']);

/**
 * Collapsible IRB resources section — appears below the request list inside a Study Workspace.
 * Collapsed by default; expanding lazy-loads the panel.
 */
function IRBStudySection() {
  const [open, setOpen] = useState(false);
  return (
    <Card className="mt-6 border-yellow-200" data-testid="study-irb-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left hover:bg-yellow-50/50 transition-colors"
        data-testid="study-irb-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-yellow-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-yellow-700" />
          </div>
          <div>
            <p className="font-semibold text-slate-900 font-['Manrope']">IRB Resources</p>
            <p className="text-xs text-slate-500">View guidance and download required templates</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">Available</Badge>
          <ChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>
      {open && (
        <div className="px-6 pb-6">
          <IRBPanel />
        </div>
      )}
    </Card>
  );
}

export default function StudyWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isCompanyRep, isAdmin, isCompanyOwner } = useAuth();
  const blockIfOwner = makeOwnerGate(isCompanyOwner);
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddRequest, setShowAddRequest] = useState(false);
  // iter167 Issue 35 — Admin-configurable cap per request type. Hydrated
  // on mount alongside the Study payload. Shape:
  //   { FEA: {mode, max_count}, CTA: {mode, max_count} }
  const [perStudyLimits, setPerStudyLimits] = useState({
    FEA: { mode: 'unlimited', max_count: 1 },
    CTA: { mode: 'unlimited', max_count: 1 },
  });
  // (iter107) Inline title edit for Admin
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  useEffect(() => {
    fetchStudy();
  }, [id]);

  const fetchStudy = async () => {
    try {
      const [studyRes, feaPslRes, ctaPslRes] = await Promise.all([
        studiesApi.get(id),
        requestTypeSettingsApi.getPerStudyLimit('FEA').catch(() => ({ data: null })),
        requestTypeSettingsApi.getPerStudyLimit('CTA').catch(() => ({ data: null })),
      ]);
      setStudy(studyRes.data);
      const norm = (r) => {
        const d = r?.data || {};
        const mode = (d.mode === 'once' || d.mode === 'fixed') ? d.mode : 'unlimited';
        const max_count = Math.max(1, parseInt(d.max_count, 10) || 1);
        return { mode, max_count };
      };
      setPerStudyLimits({
        FEA: norm(feaPslRes),
        CTA: norm(ctaPslRes),
      });
    } catch (error) {
      toast.error('Failed to load study');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Study">
        <div className="flex items-center justify-center h-64">
          <BrandLoader size="md" label="Loading study workspace…" />
        </div>
      </DashboardLayout>
    );
  }

  if (!study) {
    return (
      <DashboardLayout title="Study">
        <div className="text-center py-16">
          <p className="text-slate-500 text-lg">Study not found</p>
          <Button onClick={() => navigate('/studies')} className="mt-4">Back to Studies</Button>
        </div>
      </DashboardLayout>
    );
  }

  const requests = study.requests || [];
  const timeline = study.timeline || [];

  return (
    <DashboardLayout title="Study Workspace">
      <div className="max-w-6xl mx-auto" data-testid="study-workspace">
        {/* Header */}
        <Button variant="ghost" onClick={() => navigate('/studies')} className="mb-4">
          <ArrowLeft size={16} className="mr-2" /> Back to Studies
        </Button>

        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              {editingTitle ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    className="text-2xl font-bold h-auto py-1.5"
                    autoFocus
                    data-testid="study-title-input"
                  />
                  <Button
                    size="sm"
                    onClick={async () => {
                      const next = (titleDraft || '').trim();
                      if (!next) { toast.error('Title cannot be empty'); return; }
                      if (next === study.title) { setEditingTitle(false); return; }
                      setSavingTitle(true);
                      try {
                        await studiesApi.update(study.id, { title: next });
                        setStudy({ ...study, title: next });
                        setEditingTitle(false);
                        toast.success('Study title updated');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to update title');
                      } finally {
                        setSavingTitle(false);
                      }
                    }}
                    disabled={savingTitle}
                    data-testid="study-title-save-btn"
                    className="bg-blue-900 hover:bg-blue-950"
                  >
                    {savingTitle ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setEditingTitle(false); setTitleDraft(study.title || ''); }}
                    data-testid="study-title-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <>
                  <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']" data-testid="study-title">{study.title}</h1>
                  <Badge className={STATUS_COLORS[study.status]}>{formatStatus(study.status)}</Badge>
                  {isAdmin && isAdmin() && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-500 hover:text-blue-700"
                      onClick={() => { setTitleDraft(study.title || ''); setEditingTitle(true); }}
                      data-testid="study-title-edit-btn"
                      title="Edit study title"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>{study.company_name}</span>
              {study.specialty_names?.length > 0 && (
                <>
                  <span className="text-slate-300">|</span>
                  <span>{study.specialty_names.join(', ')}</span>
                </>
              )}
              {study.therapeutic_area && (
                <>
                  <span className="text-slate-300">|</span>
                  <span>{study.therapeutic_area}</span>
                </>
              )}
            </div>
            <p className="text-sm text-slate-400 mt-1">
              Created by {study.creator_name} on {formatDate(study.created_at)}
            </p>
          </div>
          {isCompanyRep() && (
            <Button onClick={() => { if (blockIfOwner()) return; setShowAddRequest(true); }} className="bg-blue-900 hover:bg-blue-950" data-testid="add-request-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Request
            </Button>
          )}
          {isAdmin && isAdmin() && (
            <Button
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50 ml-2"
              data-testid="delete-study-btn"
              onClick={async () => {
                const reqCount = (study.requests || []).length;
                const msg = reqCount > 0
                  ? `Permanently delete study "${study.title}" and ${reqCount} child request${reqCount === 1 ? '' : 's'} (including all assignments, signatures, documents, audit, and files on disk)?\n\nThis cannot be undone.`
                  : `Permanently delete study "${study.title}"?\n\nThis cannot be undone.`;
                if (!window.confirm(msg)) return;
                try {
                  const res = await studiesApi.adminDelete(study.id);
                  toast.success(res.data?.message || 'Study deleted');
                  navigate('/studies');
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to delete study');
                }
              }}
            >
              <XCircle className="w-4 h-4 mr-2" /> Delete Study
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="requests" className="w-full">
          <TabsList>
            <TabsTrigger value="requests">Requests ({requests.length})</TabsTrigger>
            {/* iter163e — Timeline is hidden from Company / Company Rep accounts.
                Studies as a whole are an internal workflow view; the timeline
                surfaces internal stage transitions and reviewer activity that
                should not be visible to customer accounts. */}
            {!isCompanyRep() && (
              <TabsTrigger value="timeline" data-testid="study-timeline-tab">Timeline</TabsTrigger>
            )}
          </TabsList>

          {/* Requests Tab */}
          <TabsContent value="requests" className="mt-6">
            {requests.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No requests in this study yet</p>
                {isCompanyRep() && (
                  <Button onClick={() => navigate(`/requests/new?study_id=${study.id}`)} variant="outline" className="mt-3">
                    <Plus className="w-4 h-4 mr-2" /> Create First Request
                  </Button>
                )}
              </div>
            ) : (
              <div className="relative">
                {/* Connector line */}
                {requests.length > 1 && (
                  <div className="absolute left-6 top-8 bottom-8 w-px bg-slate-200 z-0" />
                )}

                <div className="space-y-4">
                  {requests.map((req, idx) => {
                    const TypeIcon = req.type === 'FEA' ? FlaskConical : FileCheck;
                    const typeColor = req.type === 'FEA' ? 'text-teal-600 bg-teal-50 border-teal-200' : 'text-violet-600 bg-violet-50 border-violet-200';

                    return (
                      <div key={req.id} className="relative" data-testid={`study-request-${req.id}`}>
                        {/* Step circle */}
                        <div className={`absolute left-0 top-5 w-12 h-12 rounded-full border-2 flex items-center justify-center z-10 ${typeColor}`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>

                        {/* Request Card */}
                        <Card
                          className="ml-16 cursor-pointer hover:border-slate-300 hover:shadow-sm transition-all"
                          onClick={() => navigate(`/requests/${req.id}`)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className={req.type === 'FEA' ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-violet-50 text-violet-700 border-violet-200'}>
                                    {req.type}
                                  </Badge>
                                  <span className="font-mono text-xs text-slate-400">{req.ref_no}</span>
                                  <Badge className={REQ_STATUS_COLORS[req.status] || 'bg-slate-100 text-slate-500'}>
                                    {formatStatus(req.status)}
                                  </Badge>
                                </div>
                                <h3 className="font-medium text-slate-900 mb-1">{req.title}</h3>
                                <p className="text-sm text-slate-500 line-clamp-1">{req.description}</p>

                                {/* Assignments — render PI/Co-I tags; on FEA, suppress generic consultant chips but keep PI/Co-I tagged ones. Backend already filters by visible_to_company for company reps. */}
                                {(() => {
                                  const piIds = req.selected_pi_user_ids || [];
                                  const coiIds = req.selected_co_investigator_user_ids || [];
                                  const assigns = (req.assignments || []).filter(a => {
                                    if (!a.assignee_name) return false;
                                    // On FEA: hide untagged consultant/investigator chips (too many),
                                    // but keep PI/Co-I tagged ones so they remain visible at the workspace level.
                                    if (req.type === 'FEA' && (a.assignment_role === 'CONSULTANT' || a.assignment_role === 'INVESTIGATOR')) {
                                      const isPI = piIds.includes(a.assignee_user_id);
                                      const isCoI = coiIds.includes(a.assignee_user_id);
                                      if (!isPI && !isCoI) return false;
                                    }
                                    return true;
                                  });
                                  const hiddenCount = req.hidden_assignment_count || 0;
                                  if (assigns.length === 0 && hiddenCount === 0) return null;
                                  return (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                      {assigns.map((a, i) => {
                                        const isInvestigator = (a.assignment_role === 'INVESTIGATOR' || a.assignment_role === 'PI' || a.assignment_role === 'CONSULTANT');
                                        const isPI = isInvestigator && piIds.includes(a.assignee_user_id);
                                        const isCoI = isInvestigator && coiIds.includes(a.assignee_user_id);
                                        let label, chipClass;
                                        if (isPI) {
                                          label = `PI: ${a.assignee_name}`;
                                          chipClass = 'bg-amber-100 text-amber-800 border border-amber-300';
                                        } else if (isCoI) {
                                          label = `Co-I: ${a.assignee_name}`;
                                          chipClass = 'bg-blue-100 text-blue-700 border border-blue-200';
                                        } else if (a.assignment_role === 'COORDINATOR') {
                                          label = `Coordinator: ${a.assignee_name}`;
                                          chipClass = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
                                        } else if (isInvestigator) {
                                          label = `Investigator: ${a.assignee_name}`;
                                          chipClass = 'bg-violet-50 text-violet-700 border border-violet-200';
                                        } else if (a.assignment_role === 'REVIEWER') {
                                          label = `Reviewer: ${a.assignee_name}`;
                                          chipClass = 'bg-blue-50 text-blue-700 border border-blue-200';
                                        } else {
                                          label = `${a.assignment_role}: ${a.assignee_name}`;
                                          chipClass = 'bg-slate-100 text-slate-600';
                                        }
                                        return (
                                          <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${chipClass}`}>
                                            {label}
                                            {a.status === 'DONE' && <CheckCircle2 className="w-2.5 h-2.5 inline ml-0.5 text-emerald-500" />}
                                          </span>
                                        );
                                      })}
                                      {hiddenCount > 0 && (
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200">
                                          +{hiddenCount} more team member{hiddenCount === 1 ? '' : 's'} assigned
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                              <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0 mt-1" />
                            </div>
                          </CardContent>
                        </Card>

                        {/* Inheritance arrow */}
                        {idx < requests.length - 1 && (
                          <div className="ml-6 py-1 text-[10px] text-slate-400 flex items-center gap-1">
                            <div className="w-px h-3 bg-slate-200" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Timeline Tab — iter163e: only rendered when the user is not Company / Company Rep */}
          {!isCompanyRep() && (
          <TabsContent value="timeline" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Study Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-8">No activity yet</p>
                ) : (() => {
                  // Group by date
                  const grouped = {};
                  timeline.forEach(entry => {
                    const date = entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown';
                    if (!grouped[date]) grouped[date] = [];
                    grouped[date].push(entry);
                  });
                  
                  const ACTION_LABELS = {
                    create_request: 'Request Created',
                    submit_request: 'Request Submitted',
                    route_to_consultants: 'Routed to Consultants',
                    assign_consultant: 'Consultant Assigned',
                    create_assignment: 'Team Member Assigned',
                    feasibility_response: 'Consultant Response',
                    submit_review: 'Review Submitted',
                    approve_request: 'Request Approved',
                    reject_request: 'Request Rejected',
                    close_request: 'Request Closed',
                    request_changes: 'Changes Requested',
                    update_status: 'Status Updated',
                    admin_status_override: 'Admin Override',
                    signature_signed: 'Signature Completed',
                    request_executed: 'Request Executed',
                    update_specialty: 'Specialties Updated',
                    update_form_config: 'Form Config Updated',
                    document_uploaded: 'Document Uploaded',
                  };
                  
                  return (
                    <div className="space-y-6">
                      {Object.entries(grouped).map(([date, entries]) => (
                        <div key={date}>
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">{date}</span>
                            <div className="h-px flex-1 bg-slate-100" />
                          </div>
                          <div className="space-y-2 ml-2">
                            {entries.map((entry) => {
                              const isOverride = entry.action_key === 'admin_status_override';
                              const isFEA = entry.request_type === 'FEA';
                              const isCTA = entry.request_type === 'CTA';
                              const borderColor = isOverride ? 'border-l-red-400' : isFEA ? 'border-l-teal-400' : isCTA ? 'border-l-violet-400' : 'border-l-slate-200';
                              const label = ACTION_LABELS[entry.action_key] || entry.action_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                              
                              // Extract meaningful detail from payload
                              let detail = '';
                              const p = entry.payload || {};
                              if (entry.action_key === 'create_assignment' && p.assignee_name) detail = `${p.assignment_role}: ${p.assignee_name}`;
                              else if (entry.action_key === 'admin_status_override') detail = `${p.from_status} → ${p.to_status}: ${p.reason || ''}`;
                              else if (entry.action_key === 'route_to_consultants') detail = `${p.consultant_count || ''} consultant(s)`;
                              else if (entry.action_key === 'close_request') detail = p.status || '';
                              else if (entry.action_key === 'create_request') detail = p.ref_no || '';
                              else if (entry.action_key === 'submit_review') detail = p.reviewer_name || '';
                              else if (p.ref_no) detail = p.ref_no;
                              
                              return (
                                <div key={entry.id} className={`flex items-start gap-3 border-l-2 ${borderColor} pl-3 py-1.5`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {entry.request_ref && (
                                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isFEA ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-violet-50 text-violet-700 border-violet-200'}`}>
                                          {entry.request_type} {entry.request_ref}
                                        </Badge>
                                      )}
                                      <span className={`text-sm font-medium ${isOverride ? 'text-red-700' : 'text-slate-900'}`}>
                                        {label}
                                      </span>
                                    </div>
                                    {detail && (
                                      <p className={`text-xs mt-0.5 ${isOverride ? 'text-red-600' : 'text-slate-500'}`}>{detail}</p>
                                    )}
                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                      {entry.actor_name} · {formatDateTime(entry.created_at)}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
          )}
        </Tabs>

        {/* IRB Resources — visible only to Company / Company Rep (institution-wide read-only library) */}
        {isCompanyRep() && <IRBStudySection />}

        {/* Add Request Modal */}
        <Dialog open={showAddRequest} onOpenChange={setShowAddRequest}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Request to Study</DialogTitle>
              <DialogDescription>
                Select a request type. The per-study limit is set by your administrator — requests in Rejected / Withdrawn / Failed / Closed states do not count toward the cap.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {[
                { type: 'FEA', label: 'Feasibility Study', desc: 'Evaluate if your institution can support this research', icon: FlaskConical, color: 'teal' },
                { type: 'CTA', label: 'Clinical Trial Agreement', desc: 'Formal agreement for conducting a clinical trial', icon: FileCheck, color: 'violet' },
              ].map(({ type, label, desc, icon: Icon, color }) => {
                // iter167 Issue 35 — Cap check follows admin config, not a
                // hardcoded "once per study" rule. Terminal-state requests
                // (REJECTED/WITHDRAWN/FAILED/CLOSED) are excluded from the
                // active count, matching backend behaviour.
                const cfg = perStudyLimits[type] || { mode: 'unlimited', max_count: 1 };
                const activeCount = requests.filter(r => r.type === type && !TERMINAL_STATUSES.has(r.status)).length;
                const cap = cfg.mode === 'unlimited'
                  ? Infinity
                  : (cfg.mode === 'once' ? 1 : Math.max(1, cfg.max_count));
                const capReached = activeCount >= cap;
                const badgeText = cfg.mode === 'unlimited'
                  ? 'Limit reached'
                  : `${activeCount} / ${cap} used`;
                return (
                  <button
                    key={type}
                    disabled={capReached}
                    onClick={() => {
                      setShowAddRequest(false);
                      navigate(`/requests/new?study_id=${study.id}&type=${type}`);
                    }}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      capReached
                        ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
                        : `border-slate-200 hover:border-${color}-400 hover:bg-${color}-50 cursor-pointer`
                    }`}
                    data-testid={`add-${type.toLowerCase()}-btn`}
                  >
                    <Icon className={`w-6 h-6 mb-2 ${capReached ? 'text-slate-300' : `text-${color}-600`}`} />
                    <h4 className={`font-medium text-sm ${capReached ? 'text-slate-400' : 'text-slate-900'}`}>{label}</h4>
                    <p className="text-[10px] text-slate-400 mt-0.5">{desc}</p>
                    {capReached ? (
                      <Badge className="mt-2 bg-emerald-100 text-emerald-700 border border-emerald-300 text-[10px]" data-testid={`${type.toLowerCase()}-cap-reached-badge`}>
                        {badgeText}
                      </Badge>
                    ) : (
                      <Badge className={`mt-2 bg-${color}-100 text-${color}-700 text-[10px]`}>
                        {cfg.mode === 'fixed' && cfg.max_count > 1 ? `${type} · ${activeCount} / ${cap}` : type}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
